/** Shared bits for Settings section components. */
import { ReactNode, useState } from "react";
import { Button } from "@/components/ui/button";
import { Copy, Check } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

export function ComingOnline({
  title = "Coming online — Q3 2026",
  description,
}: {
  title?: string;
  description?: ReactNode;
}) {
  return (
    <div className="rounded border border-dashed border-border bg-muted/20 p-8 text-center">
      <h3 className="text-sm font-semibold text-foreground">{title}</h3>
      <p className="mt-2 text-xs text-muted-foreground max-w-md mx-auto">
        {description ?? "This admin endpoint isn't live yet. It'll appear here automatically once provisioned."}
      </p>
    </div>
  );
}

export function CopyButton({ value, className }: { value: string; className?: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <Button
      size="sm"
      variant="ghost"
      className={cn("h-7 px-2", className)}
      onClick={() => {
        navigator.clipboard.writeText(value).then(() => {
          setCopied(true);
          toast.success("Copied");
          setTimeout(() => setCopied(false), 1500);
        });
      }}
    >
      {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
    </Button>
  );
}

export function SecretReveal({ value }: { value: string }) {
  return (
    <div className="flex items-center gap-2 rounded border border-border bg-muted/40 px-3 py-2">
      <code className="flex-1 font-mono text-xs break-all">{value}</code>
      <CopyButton value={value} />
    </div>
  );
}

export function MetaRow({ k, v }: { k: string; v?: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-4 px-3 py-2 text-xs border-b border-border last:border-b-0">
      <span className="text-muted-foreground">{k}</span>
      <span className="text-foreground text-right break-all">{v ?? "—"}</span>
    </div>
  );
}

export function THead({ cols }: { cols: string[] }) {
  return (
    <thead>
      <tr className="border-b border-border bg-muted/30">
        {cols.map((h) => (
          <th
            key={h}
            className="px-3 py-2 text-left font-semibold uppercase tracking-wider text-[10px] text-muted-foreground"
          >
            {h}
          </th>
        ))}
      </tr>
    </thead>
  );
}
